/**
 * Telegram CloudStorage wrapper.
 *
 * Stores the encrypted keystore blob. CloudStorage is per-bot-per-user,
 * server-side at Telegram, and syncs across the user's TG devices.
 *
 * 100 KB total budget per user — we use ~1 KB for the keystore.
 *
 * Falls back to `localStorage` when CloudStorage is unavailable
 * (older Telegram clients, or running outside Telegram entirely).
 */

const LS_PREFIX = "teleswap:wallet:";

// Some TG clients expose CloudStorage but never invoke the callback
// (older mobile builds, embedded webviews, flaky network). Without a
// ceiling here, a wallet write hangs the CreateSheet spinner forever.
const CLOUD_TIMEOUT_MS = 10_000;

const withTimeout = <T>(p: Promise<T>, label: string): Promise<T> =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`${label} timed out after ${CLOUD_TIMEOUT_MS / 1000}s`));
    }, CLOUD_TIMEOUT_MS);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });

const cloud = (): NonNullable<TelegramWebApp["CloudStorage"]> | null =>
  window.Telegram?.WebApp?.CloudStorage ?? null;

export const isCloudAvailable = (): boolean => !!cloud();

/** Read a value. Returns null if not present. */
export const cloudGet = (key: string): Promise<string | null> => {
  const cs = cloud();
  if (cs) {
    return withTimeout(
      new Promise<string | null>((resolve, reject) => {
        cs.getItem(key, (err, value) => {
          if (err) return reject(err);
          resolve(value && value.length > 0 ? value : null);
        });
      }),
      "CloudStorage read",
    );
  }
  // localStorage fallback
  try {
    const v = localStorage.getItem(LS_PREFIX + key);
    return Promise.resolve(v && v.length > 0 ? v : null);
  } catch {
    return Promise.resolve(null);
  }
};

/** Write a value. */
export const cloudSet = (key: string, value: string): Promise<void> => {
  const cs = cloud();
  if (cs) {
    return withTimeout(
      new Promise<void>((resolve, reject) => {
        cs.setItem(key, value, (err) => {
          if (err) return reject(err);
          resolve();
        });
      }),
      "CloudStorage write",
    );
  }
  try {
    localStorage.setItem(LS_PREFIX + key, value);
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

/** Delete a value. */
export const cloudRemove = (key: string): Promise<void> => {
  const cs = cloud();
  if (cs) {
    return withTimeout(
      new Promise<void>((resolve, reject) => {
        cs.removeItem(key, (err) => {
          if (err) return reject(err);
          resolve();
        });
      }),
      "CloudStorage delete",
    );
  }
  try {
    localStorage.removeItem(LS_PREFIX + key);
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};
